import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type PeriodMode = 'month' | 'range';
type Scope = 'installments' | 'new_sales';
type ExportFormat = 'pdf' | 'xlsx';

type Props = {
    open: boolean;
    onClose: () => void;
};

function currentMonthValue(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function CommissionPeriodReportModal({ open, onClose }: Props) {
    const { t } = useI18n();
    const [periodMode, setPeriodMode] = useState<PeriodMode>('month');
    const [month, setMonth] = useState(currentMonthValue);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [scope, setScope] = useState<Scope>('installments');
    const [loadingFormat, setLoadingFormat] = useState<ExportFormat | null>(null);

    const scopeHint = useMemo(
        () => (scope === 'installments'
            ? t('v2.ploomes_deals.commission_report.scope_hint_installments')
            : t('v2.ploomes_deals.commission_report.scope_hint_new_sales')),
        [scope, t],
    );

    const canSubmit = periodMode === 'month'
        ? Boolean(month)
        : Boolean(from && to && from <= to);

    const handleGenerate = async (format: ExportFormat) => {
        if (!canSubmit || loadingFormat) return;
        setLoadingFormat(format);
        try {
            const payload = periodMode === 'month'
                ? { period_mode: 'month', month, scope, format }
                : { period_mode: 'range', from, to, scope, format };

            const response = await api.post('/commissioning/seller-period-report', payload, {
                validateStatus: (s) => s < 500,
            });

            if (response.status >= 400) {
                const message = (response.data as { message?: string })?.message
                    || t('v2.ploomes_deals.commission_report.error');
                toast.error(message);
                return;
            }

            const message = (response.data as { message?: string })?.message
                || t('v2.ploomes_deals.commission_report.success');
            toast.success(message);
            onClose();
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { message?: string } } };
            toast.error(
                ax.response?.data?.message || t('v2.ploomes_deals.commission_report.error'),
            );
        } finally {
            setLoadingFormat(null);
        }
    };

    const loading = loadingFormat !== null;

    return (
        <V2Modal
            open={open}
            onClose={() => { if (!loading) onClose(); }}
            title={t('v2.ploomes_deals.commission_report.title')}
            description={t('v2.ploomes_deals.commission_report.description')}
            size="lg"
            footer={(
                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        {t('v2.common.cancel')}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => void handleGenerate('xlsx')}
                        isLoading={loadingFormat === 'xlsx'}
                        disabled={!canSubmit || loading}
                    >
                        {loadingFormat === 'xlsx'
                            ? t('v2.ploomes_deals.commission_report.generating')
                            : t('v2.ploomes_deals.commission_report.generate_excel')}
                    </Button>
                    <Button
                        onClick={() => void handleGenerate('pdf')}
                        isLoading={loadingFormat === 'pdf'}
                        disabled={!canSubmit || loading}
                    >
                        {loadingFormat === 'pdf'
                            ? t('v2.ploomes_deals.commission_report.generating')
                            : t('v2.ploomes_deals.commission_report.generate_pdf')}
                    </Button>
                </div>
            )}
        >
            <div className="space-y-4 px-5 py-4">
                <fieldset className="space-y-2">
                    <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('v2.ploomes_deals.commission_report.period_mode')}
                    </legend>
                    <div className="flex flex-wrap gap-3">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-200">
                            <input
                                type="radio"
                                name="period_mode"
                                checked={periodMode === 'month'}
                                onChange={() => setPeriodMode('month')}
                            />
                            {t('v2.ploomes_deals.commission_report.period_mode_month')}
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-200">
                            <input
                                type="radio"
                                name="period_mode"
                                checked={periodMode === 'range'}
                                onChange={() => setPeriodMode('range')}
                            />
                            {t('v2.ploomes_deals.commission_report.period_mode_range')}
                        </label>
                    </div>
                </fieldset>

                {periodMode === 'month' ? (
                    <label className="block space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-zinc-400">
                            {t('v2.ploomes_deals.commission_report.month')}
                        </span>
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block space-y-1">
                            <span className="text-xs font-medium text-gray-600 dark:text-zinc-400">
                                {t('v2.ploomes_deals.commission_report.from')}
                            </span>
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                            />
                        </label>
                        <label className="block space-y-1">
                            <span className="text-xs font-medium text-gray-600 dark:text-zinc-400">
                                {t('v2.ploomes_deals.commission_report.to')}
                            </span>
                            <input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                            />
                        </label>
                    </div>
                )}

                <fieldset className="space-y-2">
                    <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('v2.ploomes_deals.commission_report.scope')}
                    </legend>
                    <div className="space-y-2">
                        <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-zinc-200">
                            <input
                                type="radio"
                                name="scope"
                                className="mt-1"
                                checked={scope === 'installments'}
                                onChange={() => setScope('installments')}
                            />
                            <span>{t('v2.ploomes_deals.commission_report.scope_installments')}</span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-zinc-200">
                            <input
                                type="radio"
                                name="scope"
                                className="mt-1"
                                checked={scope === 'new_sales'}
                                onChange={() => setScope('new_sales')}
                            />
                            <span>{t('v2.ploomes_deals.commission_report.scope_new_sales')}</span>
                        </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">{scopeHint}</p>
                </fieldset>
            </div>
        </V2Modal>
    );
}
